// apps/client/src/characters/part.ts — the doll's part toolkit (§8.5): the shared matte toy material, a mesh-making helper and colour shades.
// A dressed doll is baked into one vertex-coloured skinned mesh (doll.ts `bakeDoll`), so a part's material only carries its colour there.
import * as THREE from 'three';

const mats = new Map<string, THREE.MeshStandardMaterial>();
/** The shared matte toy material, cached per colour (key = upper-cased hex). */
export function toyMaterial(hex: string): THREE.MeshStandardMaterial {
  const k = hex.toUpperCase();
  let m = mats.get(k);
  if (!m) { m = new THREE.MeshStandardMaterial({ color: k, roughness: 0.8, metalness: 0 }); mats.set(k, m); }
  return m;
}

export type V3 = readonly [number, number, number];
/** A shaded, shadow-casting part in `hex` at `at` under `parent`, optionally named (tests and NPC code find parts by name). */
export function part(parent: THREE.Object3D, geo: THREE.BufferGeometry, hex: string, at: V3 = [0, 0, 0], name = ''): THREE.Mesh {
  const m = new THREE.Mesh(geo, toyMaterial(hex));
  m.position.set(...at);
  m.castShadow = true;
  m.receiveShadow = true;
  m.name = name;
  parent.add(m);
  return m;
}
/** An empty named group under `parent` (a body slot, or a tag such as `species:duck`). */
export function group(parent: THREE.Object3D, name: string, at: V3 = [0, 0, 0]): THREE.Group {
  const g = new THREE.Group();
  g.name = name;
  g.position.set(...at);
  parent.add(g);
  return g;
}

/** `hex` with its lightness scaled by `f` (< 1 deeper, > 1 paler, toward white). */
export function shade(hex: string, f: number): string {
  const c = new THREE.Color(hex), hsl = { h: 0, s: 0, l: 0 };
  c.getHSL(hsl);
  const l = f <= 1 ? hsl.l * f : hsl.l + (1 - hsl.l) * (f - 1);
  return `#${c.setHSL(hsl.h, hsl.s, Math.min(1, Math.max(0, l))).getHexString().toUpperCase()}`;
}
/** Perceived lightness 0–1 (sRGB luma), for picking a readable eye colour on a dark face. */
export function luma(hex: string): number {
  const c = new THREE.Color(hex).convertLinearToSRGB();
  return 0.299 * c.r + 0.587 * c.g + 0.114 * c.b;
}
